// @ts-nocheck
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  X, 
  ChevronLeft, 
  ChevronRight, 
  Check, 
  AlertCircle, 
  Users, 
  Mail, 
  Settings, 
  Calendar,
  AlertTriangle,
  Brain,
  Eye,
  Zap,
  Target,
  Clock,
  TrendingUp,
  Sparkles,
  Play,
  BarChart3
} from 'lucide-react';
import { fetchUserTemplates, getCustomerGroupsRes, identityManagementRes } from 'core';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from 'components/ui/select';
import { SelectField } from 'components/ui/select-field';

/*
 * This modal collected a campaign, called `onSave`, and reported success —
 * while App.tsx's handler only console.logged. Nothing was ever created.
 *
 * Making it real needed four things it did not have: live audiences, live
 * templates, a verified sender address and a business name. The last two are
 * required by `POST campaigns/unary` (see CampaignForm.tsx:520-531, the path
 * that always worked) and the form never asked for them.
 *
 * Props still win when a caller supplies them; otherwise the modal loads its
 * own data, so it works no matter who renders it. App.tsx passed
 * `segments={[]}` and `templates={[]}`, which is why both pickers were empty.
 */
/* One shape, one place. This literal existed twice — once as the useState
   initialiser and once inside the reset-on-open effect — so the two drifted the
   moment a field was added: `friendlyName` was initialised but not reset, and
   validateStep called .trim() on undefined the next time the modal opened. */
const EMPTY_FORM = {
  // Step 1: Basic Information
  name: '',
  description: '',
  type: 'promotional',

  // Required by POST campaigns/unary — the form never asked for either,
  // which is one reason it could not have been submitting anything.
  mailToUse: '',
  friendlyName: '',

  // Step 2: Audience Selection
  segmentId: '',
  estimatedReach: 0,

  // Step 3: Template Selection
  templateId: '',
  templatePreview: null,

  // Step 4: AI Configuration & Scheduling
  schedulingType: 'immediate', // 'immediate' | 'scheduled'
  scheduledAt: '',

  /* Step 4: A/B test. `POST campaigns/ab-testing` takes no top-level template
     or subject — each variant carries its own — so variant A reuses the
     template chosen on step 3 and only needs a subject of its own. */
  abTestingEnabled: false,
  subjectA: '',
  variantBTemplateId: '',
  subjectB: '',
  testPercentage: 50,
  evaluationHours: 24,
  winnerMetric: 'opens',
};

/** The backend rejects these outright in a campaign name. */
// eslint-disable-next-line no-useless-escape
const FORBIDDEN_NAME_CHARS = /[!#$%^&*()<>?\/\\|}{~:;"]/;
const NAME_MAX = 50;
const SUBJECT_MAX = 100;
/** `friendlyName` and `mailToUse` are stored together as `Name <email>`. */
const SENDER_COMBINED_MAX = 97;

/**
 * The A/B rules `POST campaigns/ab-testing` enforces, checked here first.
 *
 * The endpoint answers a broken field with a bare string and a 424, so finding
 * out in the form is the difference between a label under the input and a
 * round trip that says "Invalid test percentage."
 */
/**
 * True when the two variants are the same test twice over.
 *
 * Not an error — the endpoint accepts it, and an identical pair is a valid if
 * pointless experiment. But the whole audience is charged for a comparison
 * that cannot tell the two apart, and a tie goes to A, so it is worth saying
 * before the credits are spent.
 */
export const variantsAreIdentical = (formData) =>
  !!formData.templateId &&
  String(formData.templateId) === String(formData.variantBTemplateId) &&
  String(formData.subjectA || '').trim() !== '' &&
  String(formData.subjectA || '').trim() === String(formData.subjectB || '').trim();

export const validateABFields = (formData) => {
  const errors = {};

  if (!String(formData.subjectA || '').trim()) {
    errors.subjectA = 'Variant A needs a subject line';
  } else if (formData.subjectA.trim().length > SUBJECT_MAX) {
    errors.subjectA = `Subject cannot be longer than ${SUBJECT_MAX} characters`;
  }

  if (!formData.variantBTemplateId) {
    errors.variantBTemplateId = 'Choose a template for variant B';
  }

  if (!String(formData.subjectB || '').trim()) {
    errors.subjectB = 'Variant B needs a subject line';
  } else if (formData.subjectB.trim().length > SUBJECT_MAX) {
    errors.subjectB = `Subject cannot be longer than ${SUBJECT_MAX} characters`;
  }

  /* The backend accepts 0, but then nobody receives a test send and the
     "winner" is decided on no evidence at all. */
  const share = Number(formData.testPercentage);
  if (!Number.isInteger(share) || share < 1 || share > 100) {
    errors.testPercentage = 'Test share must be a whole number between 1 and 100';
  }

  const hours = Number(formData.evaluationHours);
  if (!Number.isInteger(hours) || hours < 1) {
    errors.evaluationHours = 'Evaluation window must be at least 1 hour';
  }

  if (formData.winnerMetric !== 'opens' && formData.winnerMetric !== 'clicks') {
    errors.winnerMetric = 'Choose opens or clicks';
  }

  return errors;
};

/**
 * The request body, and which endpoint it is for.
 *
 * Two endpoints, two shapes, and they are not interchangeable: an A/B campaign
 * carries a template and subject *per variant* and none at the top level,
 * while an ordinary one is the reverse. Kept out of the submit handler so the
 * shape can be checked without driving four wizard steps.
 *
 * `abTest` is what tells the caller where to post. Deriving that from the
 * presence of `variantA` would work until the day a field is renamed.
 */
export function buildCampaignBody(formData) {
  const startsAt = formData.schedulingType === 'scheduled' && formData.scheduledAt
    ? new Date(formData.scheduledAt)
    : new Date();
  const endsAt = new Date(startsAt.getTime() + 24 * 60 * 60 * 1000);

  const common = {
    name: formData.name.trim(),
    sdate: startsAt.toISOString(),
    edate: endsAt.toISOString(),
    mailToUse: formData.mailToUse,
    friendlyName: formData.friendlyName.trim(),
  };

  if (formData.abTestingEnabled) {
    return {
      abTest: true,
      api: {
        ...common,
        // The A/B endpoint validates group ids numerically.
        cgrp: formData.segmentId ? [Number(formData.segmentId)] : [],
        testPercentage: Number(formData.testPercentage),
        evaluationHours: Number(formData.evaluationHours),
        winnerMetric: formData.winnerMetric,
        variantA: {
          // Variant A is the template chosen on step 3.
          tid: Number(formData.templateId),
          subject: formData.subjectA.trim(),
        },
        variantB: {
          tid: Number(formData.variantBTemplateId),
          subject: formData.subjectB.trim(),
        },
      },
    };
  }

  return {
    abTest: false,
    api: {
      ...common,
      subject: formData.description.trim() || formData.name.trim(),
      tid: formData.templateId,
      cgrp: formData.segmentId ? [formData.segmentId] : [],
    },
  };
}

const CreateCampaignModal = ({
  isOpen,
  onClose,
  onSave,
  segments = [],
  templates = [],
  aiAgents = [],
  existingCampaigns = []
}) => {
  // Multi-step form state
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Live data, loaded when the caller does not supply it
  const [loadedSegments, setLoadedSegments] = useState([]);
  const [loadedTemplates, setLoadedTemplates] = useState([]);
  const [identities, setIdentities] = useState([]);
  const [loadingRefs, setLoadingRefs] = useState(false);
  const [refsError, setRefsError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  /* Loads audiences, templates and verified senders the same way CampaignForm
     does, so both paths agree on what exists. Only runs while open, and only
     for the lists the caller did not already provide. */
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoadingRefs(true);
    setRefsError(null);

    Promise.all([
      segments.length ? Promise.resolve(null) : getCustomerGroupsRes(),
      templates.length ? Promise.resolve(null) : fetchUserTemplates(),
      identityManagementRes(),
    ])
      .then(([groupRes, templateRes, identityRes]) => {
        if (cancelled) return;

        if (groupRes) {
          const active = (groupRes || []).filter(
            (g) => g.leadsCount > 0 && g.status === 'ACTIVE'
          );
          setLoadedSegments(
            active.map((g) => ({ id: String(g.id), name: g.name, size: g.leadsCount }))
          );
        }

        if (templateRes) {
          setLoadedTemplates(
            (templateRes?.items ?? []).map((t) => ({
              id: String(t.id),
              name: t.name || `Template #${t.id}`,
            }))
          );
        }

        // Only verified senders — an unverified address is rejected on send.
        const raw = identityRes?.data || {};
        setIdentities(
          Object.keys(raw)
            .map((k) => ({ id: k, ...raw[k] }))
            .filter((i) => i.status === 'Success')
            .map((i) => ({ value: String(i.email), text: i.email }))
        );
      })
      .catch((err) => {
        if (!cancelled) setRefsError(err ?? new Error('Could not load campaign options'));
      })
      .finally(() => {
        if (!cancelled) setLoadingRefs(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, segments.length, templates.length]);

  // Caller-supplied lists win; otherwise use what we loaded.
  const availableSegments = segments.length ? segments : loadedSegments;
  const availableTemplates = templates.length ? templates : loadedTemplates;

  // Form data state
  const [formData, setFormData] = useState({ ...EMPTY_FORM });

  // Step configuration
  const steps = [
    {
      id: 1,
      title: 'Basic Information',
      description: 'Campaign details and type',
      icon: Mail,
      isComplete: false
    },
    {
      id: 2,
      title: 'Audience Selection',
      description: 'Choose target segment',
      icon: Users,
      isComplete: false
    },
    {
      id: 3,
      title: 'Template Selection',
      description: 'Pick email template',
      icon: Eye,
      isComplete: false
    },
    {
      id: 4,
      title: 'Testing & Scheduling',
      description: 'Test and send',
      icon: Settings,
      isComplete: false
    }
  ];

  // Campaign type options
  const campaignTypes = [
    { value: 'promotional', label: 'Promotional', icon: Target, description: 'Sales and marketing campaigns' },
    { value: 'automated', label: 'Automated', icon: Zap, description: 'Trigger-based sequences' },
    { value: 'announcement', label: 'Announcement', icon: Sparkles, description: 'Product updates and news' },
    { value: 'newsletter', label: 'Newsletter', icon: Mail, description: 'Regular content updates' },
    { value: 'welcome', label: 'Welcome', icon: Users, description: 'New subscriber onboarding' },
    { value: 'sequence', label: 'Sequence', icon: BarChart3, description: 'Multi-email workflows' }
  ];

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setFormData({ ...EMPTY_FORM });
      setValidationErrors({});
      setIsSubmitting(false);
    }
  }, [isOpen]);

  // Update estimated reach when segment changes
  useEffect(() => {
    if (formData.segmentId) {
      const selectedSegment = availableSegments.find(s => s.id === formData.segmentId);
      if (selectedSegment) {
        setFormData(prev => ({
          ...prev,
          estimatedReach: selectedSegment.size || selectedSegment.count || 0
        }));
      }
    }
  }, [formData.segmentId, segments]);

  // Validation functions
  const validateStep = useCallback((stepNumber) => {
    const errors = {};

    switch (stepNumber) {
      case 1:
        /* The same rules the save endpoints enforce. Breaking one of them
           otherwise costs a round trip to be told in a bare string. */
        if (!formData.name.trim()) {
          errors.name = 'Campaign name is required';
        } else if (formData.name.trim().length < 3) {
          errors.name = 'Campaign name must be at least 3 characters';
        } else if (formData.name.trim().length > NAME_MAX) {
          errors.name = `Campaign name cannot be longer than ${NAME_MAX} characters`;
        } else if (FORBIDDEN_NAME_CHARS.test(formData.name)) {
          errors.name = 'Campaign name cannot contain ! # $ % ^ & * ( ) < > ? / \\ | } { ~ : ; "';
        } else if (existingCampaigns.some(c => c.name.toLowerCase() === formData.name.trim().toLowerCase())) {
          errors.name = 'A campaign with this name already exists';
        }
        
        if (!formData.type) {
          errors.type = 'Campaign type is required';
        }

        // Both are required by the create endpoint. Validating them here, on
        // the step that collects them, means you find out on step 1 rather
        // than after filling in four steps.
        if (!formData.mailToUse) {
          errors.mailToUse = 'Choose a verified sending address';
        }
        if (!formData.friendlyName.trim()) {
          errors.friendlyName = 'Business name is required — recipients see it in their inbox';
        } else if (
          formData.friendlyName.trim().length + (formData.mailToUse || '').length > SENDER_COMBINED_MAX
        ) {
          // Stored as `Name <email>`, and the pair has one limit, not two.
          errors.friendlyName = `Business name and sending address together cannot be longer than ${SENDER_COMBINED_MAX} characters`;
        }
        break;

      case 2:
        if (!formData.segmentId) {
          errors.segmentId = 'Please select an audience segment';
        } else {
          const selectedSegment = availableSegments.find(s => s.id === formData.segmentId);
          if (selectedSegment && selectedSegment.size < 10) {
            errors.segmentId = 'Segment size too small for statistical significance (minimum 10 contacts)';
          }
        }
        break;

      case 3:
        if (!formData.templateId) {
          errors.templateId = 'Please select an email template';
        }
        break;

      case 4:
        if (formData.schedulingType === 'scheduled' && !formData.scheduledAt) {
          errors.scheduledAt = 'Please select a send date and time';
        } else if (formData.schedulingType === 'scheduled' && new Date(formData.scheduledAt) <= new Date()) {
          errors.scheduledAt = 'Scheduled time must be in the future';
        }
        
        /* Only ask for an agent when there is one to pick. The list is empty
           in every environment today, so requiring it made the step
           unsubmittable the moment AI was switched on. */
        if (formData.abTestingEnabled) {
          Object.assign(errors, validateABFields(formData));
        }
        break;

      default:
        break;
    }

    return errors;
  }, [formData, availableSegments, existingCampaigns]);

  // Check if current step is valid
  const isStepValid = useMemo(() => {
    const errors = validateStep(currentStep);
    return Object.keys(errors).length === 0;
  }, [currentStep, validateStep]);

  /* A step is done only once it is behind you AND still validates.
     Validating alone was not enough: step 4 asks for nothing until you choose
     to schedule, so it passed on the very first render and drew a green tick
     on a step nobody had reached — one `isAccessible` then refused to open.
     The validity half keeps the tick honest afterwards: `existingCampaigns`
     is a live list, so a name that was free when you left step 1 can be taken
     while you are on step 3, and the tick should go with it. */
  const updatedSteps = useMemo(() => {
    return steps.map(step => ({
      ...step,
      isComplete: step.id < currentStep && Object.keys(validateStep(step.id)).length === 0
    }));
  }, [steps, currentStep, validateStep]);

  // Handle form field changes
  const handleFieldChange = useCallback((field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      return next;
    });
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [validationErrors]);

  // Navigation handlers
  const handleNext = useCallback(() => {
    const errors = validateStep(currentStep);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    setValidationErrors({});
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  }, [currentStep, steps.length, validateStep]);

  const handlePrevious = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setValidationErrors({});
    }
  }, [currentStep]);

  const handleStepClick = useCallback((stepNumber) => {
    // Allow navigation to previous steps or current step
    if (stepNumber <= currentStep) {
      setCurrentStep(stepNumber);
      setValidationErrors({});
    }
  }, [currentStep]);

  // Form submission
  const handleSubmit = useCallback(async () => {
    // Validate all steps
    const allErrors = {};
    for (let i = 1; i <= steps.length; i++) {
      const stepErrors = validateStep(i);
      Object.assign(allErrors, stepErrors);
    }

    if (Object.keys(allErrors).length > 0) {
      setValidationErrors(allErrors);
      // Navigate to first step with errors
      for (let i = 1; i <= steps.length; i++) {
        const stepErrors = validateStep(i);
        if (Object.keys(stepErrors).length > 0) {
          setCurrentStep(i);
          break;
        }
      }
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Prepare campaign data
      const selectedSegment = availableSegments.find(s => s.id === formData.segmentId);
      const selectedTemplate = availableTemplates.find(t => t.id === formData.templateId);

      /* The shape `POST campaigns/unary` actually accepts, mirroring the
         payload CampaignForm.tsx:520-531 has always sent. Everything below it
         is the richer client-side object the modal already produced; it is
         kept so nothing that reads `campaignData` loses a field, but `api` is
         what gets persisted.

         `sdate`/`edate` follow CampaignForm's ISO encoding. An immediate send
         starts now; a scheduled one starts at the chosen time. The window ends
         a day later, matching the only two paths that create campaigns today. */
      const { api: apiBody, abTest } = buildCampaignBody(formData);

      const campaignData = {
        api: apiBody,
        abTest,

        id: `campaign-${Date.now()}`,
        name: formData.name.trim(),
        description: formData.description.trim(),
        type: formData.type,
        status: formData.schedulingType === 'immediate' ? 'active' : 'scheduled',
        
        // Template and segment info
        templateId: formData.templateId,
        templateName: selectedTemplate?.name || 'Unknown Template',
        segmentId: formData.segmentId,
        segmentName: selectedSegment?.name || 'Unknown Segment',
        estimatedReach: formData.estimatedReach,
        
        // Performance metrics (initialized)
        metrics: {
          sent: 0,
          delivered: 0,
          opened: 0,
          clicked: 0,
          converted: 0,
          revenue: 0,
          openRate: 0,
          clickRate: 0,
          conversionRate: 0,
          roi: 0,
          bounceRate: 0,
          unsubscribeRate: 0
        },
        
        // AI configuration
        
        /* A/B testing. There used to be an `abTestId` minted here from
           `Date.now()`; nothing ever sent it and no such id existed on the
           server, so it was a made-up identifier sitting in the object that
           views read. The save response carries no id either, so there is
           nothing truthful to put here. */
        abTestingEnabled: formData.abTestingEnabled,
        
        // Scheduling
        scheduledAt: formData.schedulingType === 'scheduled' ? new Date(formData.scheduledAt) : null,
        sentAt: formData.schedulingType === 'immediate' ? new Date() : null,
        
        // Automation triggers
        
        // Metadata
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: 'current-user', // This would come from auth context
      };

      await onSave(campaignData);
      onClose();
    } catch (error) {
      console.error('Error creating campaign:', error);
      setValidationErrors({ submit: 'Failed to create campaign. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, segments, templates, steps.length, validateStep, onSave, onClose]);

  // Close handler
  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      onClose();
    }
  }, [isSubmitting, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 dark:bg-black/75 backdrop-blur-[3px] flex items-center justify-center z-50 p-4">
      <div className="bg-surface dark:bg-dark-800 border border-line dark:border-dark-700 rounded-2xl shadow-2xl dark:shadow-[0_20px_60px_rgba(0,0,0,0.5)] w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-line">
          <div>
            <h2 className="text-xl font-semibold text-content">Create New Campaign</h2>
            <p className="text-sm text-content-secondary mt-1">
              Step {currentStep} of {steps.length}: {updatedSteps[currentStep - 1]?.title}
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="text-content-subtle hover:text-content-secondary transition-colors disabled:opacity-50"
            aria-label="Close modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-line">
          <div className="flex items-center justify-between">
            {updatedSteps.map((step, index) => {
              const Icon = step.icon;
              const isActive = step.id === currentStep;
              const isCompleted = step.isComplete;
              const isAccessible = step.id <= currentStep;

              return (
                <div key={step.id} className="flex items-center">
                  <button
                    onClick={() => handleStepClick(step.id)}
                    disabled={!isAccessible || isSubmitting}
                    aria-current={isActive ? 'step' : undefined}
                    className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                      isActive
                        ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 border-2 border-blue-200 dark:border-blue-800/50'
                        : isCompleted
                        ? 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-300 hover:bg-green-100'
                        : isAccessible
                        ? 'text-content-secondary hover:bg-gray-50 dark:hover:bg-dark-700'
                        : 'text-content-subtle cursor-not-allowed'
                    }`}
                  >
                    <div className={`p-2 rounded-full ${
                      isActive
                        ? 'bg-blue-100'
                        : isCompleted
                        ? 'bg-green-100'
                        : 'bg-gray-100 dark:bg-dark-700'
                    }`}>
                      {isCompleted ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Icon className="w-4 h-4" />
                      )}
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-medium">{step.title}</div>
                      <div className="text-xs opacity-75">{step.description}</div>
                      {/* The tick and the green are the only sign a step is
                          done, and neither reaches a screen reader. */}
                      {isCompleted && <span className="sr-only">Completed</span>}
                    </div>
                  </button>
                  {index < updatedSteps.length - 1 && (
                    <ChevronRight className="w-4 h-4 text-content-subtle mx-2" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {currentStep === 1 && (
            <BasicInformationStep
              formData={formData}
              onFieldChange={handleFieldChange}
              campaignTypes={campaignTypes}
              validationErrors={validationErrors}
              identities={identities}
              loadingRefs={loadingRefs}
            />
          )}
          
          {currentStep === 2 && (
            <AudienceSelectionStep
              formData={formData}
              onFieldChange={handleFieldChange}
              segments={availableSegments}
              validationErrors={validationErrors}
            />
          )}
          
          {currentStep === 3 && (
            <TemplateSelectionStep
              formData={formData}
              onFieldChange={handleFieldChange}
              templates={availableTemplates}
              validationErrors={validationErrors}
            />
          )}
          
          {currentStep === 4 && (
            <AIConfigurationStep
              formData={formData}
              onFieldChange={handleFieldChange}
              templates={availableTemplates}
              audienceSize={formData.estimatedReach}
              validationErrors={validationErrors}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-line">
          <div className="flex items-center gap-2">
            {validationErrors.submit && (
              <div className="flex items-center gap-2 text-red-600 dark:text-red-300">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">{validationErrors.submit}</span>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 1 || isSubmitting}
              className="px-4 py-2 border border-line-strong rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4 inline mr-1" />
              Previous
            </button>
            
            {currentStep < steps.length ? (
              <button
                onClick={handleNext}
                disabled={!isStepValid || isSubmitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <ChevronRight className="w-4 h-4 inline ml-1" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!isStepValid || isSubmitting}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Create Campaign
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateCampaignModal;

// Step 1: Basic Information Component
const BasicInformationStep = ({ formData, onFieldChange, campaignTypes, validationErrors, identities = [], loadingRefs = false }) => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-content mb-4">Campaign Details</h3>
        <p className="text-sm text-content-secondary mb-6">
          Start by providing basic information about your campaign.
        </p>
      </div>

      {/* Campaign Name */}
      <div>
        <label htmlFor="campaign-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Campaign Name *
        </label>
        <input
          id="campaign-name"
          type="text"
          value={formData.name}
          onChange={(e) => onFieldChange('name', e.target.value)}
          placeholder="Enter a descriptive campaign name"
          aria-invalid={!!validationErrors.name}
          className={`input-field ${
            validationErrors.name ? '!border-red-400 dark:!border-red-500/60' : ''
          }`}
        />
        {validationErrors.name && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-300 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {validationErrors.name}
          </p>
        )}
      </div>

      {/* Campaign Description */}
      <div>
        <label htmlFor="campaign-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Description
        </label>
        <textarea
          id="campaign-description"
          value={formData.description}
          onChange={(e) => onFieldChange('description', e.target.value)}
          placeholder="Describe the purpose and goals of this campaign"
          rows={3}
          className="input-field resize-y"
        />
      </div>

      {/* Campaign Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Campaign Type *
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {campaignTypes.map((type) => {
            const Icon = type.icon;
            const isSelected = formData.type === type.value;
            
            return (
              <button
                key={type.value}
                type="button"
                onClick={() => onFieldChange('type', type.value)}
                className={`p-4 border-2 rounded-lg text-left transition-all ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    : 'border-line hover:border-line-strong hover:bg-gray-50 dark:hover:bg-dark-700'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${
                    isSelected ? 'bg-blue-100' : 'bg-gray-100 dark:bg-dark-700'
                  }`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">{type.label}</div>
                    <div className="text-sm opacity-75 mt-1">{type.description}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        {validationErrors.type && (
          <p className="mt-2 text-sm text-red-600 dark:text-red-300 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {validationErrors.type}
          </p>
        )}
      </div>

      {/* A Tags field used to sit here. Neither save endpoint carries tags,
          so nothing was stored and the hint under it — "Tags help organize and
          filter your campaigns" — was not true of anything. */}

      {/* ── Sender ──
          Required by the create endpoint. The form never asked for either
          field, so nothing it collected could have been submitted. Only
          verified addresses are offered — an unverified sender is rejected
          at send time, which is a much worse place to discover it. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-line">
        <div>
          <label htmlFor="campaign-sender" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Send from <span className="text-danger" aria-hidden="true">*</span>
          </label>
          {/* Shared Select. CampaignForm already picks a sender through this
              same component, so the two paths that create a campaign no longer
              render two different dropdowns for the identical choice. */}
          <Select
            value={formData.mailToUse || undefined}
            onValueChange={(v) => onFieldChange('mailToUse', v)}
            disabled={loadingRefs || identities.length === 0}
          >
            <SelectTrigger id="campaign-sender" className="w-full">
              <SelectValue
                placeholder={
                  loadingRefs
                    ? 'Loading verified senders…'
                    : identities.length === 0
                      ? 'No verified sender yet'
                      : 'Choose a verified sender'
                }
              />
            </SelectTrigger>
            <SelectContent>
              {identities.map((identity) => (
                <SelectItem key={identity.value} value={identity.value}>{identity.text}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!loadingRefs && identities.length === 0 && (
            <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
              Verify a sending address under Identity first — campaigns cannot be sent without one.
            </p>
          )}
          {validationErrors.mailToUse && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors.mailToUse}</p>
          )}
        </div>

        <div>
          <label htmlFor="campaign-friendly-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Business name <span className="text-danger" aria-hidden="true">*</span>
          </label>
          <input
            id="campaign-friendly-name"
            type="text"
            value={formData.friendlyName}
            onChange={(e) => onFieldChange('friendlyName', e.target.value)}
            placeholder="The name recipients see in their inbox"
            className="input-field"
          />
          {validationErrors.friendlyName && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors.friendlyName}</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Step 2: Audience Selection Component
const AudienceSelectionStep = ({ formData, onFieldChange, segments, validationErrors }) => {
  const selectedSegment = segments.find(s => s.id === formData.segmentId);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-content mb-4">Choose Your Audience</h3>
        <p className="text-sm text-content-secondary mb-6">
          Select the audience segment that will receive this campaign.
        </p>
      </div>

      {/* Segment Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Audience Segment *
        </label>
        
        {segments.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 dark:bg-dark-700 rounded-lg border-2 border-dashed border-line-strong">
            <Users className="w-12 h-12 mx-auto mb-4 text-content-subtle" />
            <h4 className="text-lg font-medium text-content mb-2">No Segments Available</h4>
            <p className="text-content-secondary mb-4">
              You need to create audience segments before creating campaigns.
            </p>
            <button
              type="button"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Users className="w-4 h-4" />
              Create Segment
            </button>
          </div>
        ) : (
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {segments.map((segment) => {
              const isSelected = formData.segmentId === segment.id;
              const segmentSize = segment.size || segment.count || 0;
              const isValidSize = segmentSize >= 10;
              
              return (
                <button
                  key={segment.id}
                  type="button"
                  onClick={() => onFieldChange('segmentId', segment.id)}
                  disabled={!isValidSize}
                  className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50'
                      : isValidSize
                      ? 'border-line hover:border-line-strong hover:bg-gray-50 dark:hover:bg-dark-700'
                      : 'border-line bg-gray-50 dark:bg-dark-700 opacity-50 cursor-not-allowed'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-content">{segment.name}</h4>
                        {segment.aiGenerated && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs rounded-full">
                            <Brain className="w-3 h-3" />
                            AI Generated
                          </span>
                        )}
                      </div>
                      {segment.description && (
                        <p className="text-sm text-content-secondary mt-1">{segment.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-sm text-content-muted">
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {segmentSize.toLocaleString()} contacts
                        </span>
                        {segment.growth && (
                          <span className="flex items-center gap-1">
                            <TrendingUp className="w-4 h-4" />
                            {segment.growth} growth
                          </span>
                        )}
                      </div>
                      {!isValidSize && (
                        <p className="text-xs text-red-600 dark:text-red-300 mt-1">
                          Segment too small for statistical significance (minimum 10 contacts)
                        </p>
                      )}
                    </div>
                    <div className="ml-4">
                      {isSelected && (
                        <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
        
        {validationErrors.segmentId && (
          <p className="mt-2 text-sm text-red-600 dark:text-red-300 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {validationErrors.segmentId}
          </p>
        )}
      </div>

      {/* Estimated Reach */}
      {selectedSegment && (
        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800/50 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 dark:text-blue-300 mb-2">Campaign Reach Estimate</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-blue-700 dark:text-blue-300">Target Audience:</span>
              <div className="font-semibold text-blue-900 dark:text-blue-300">{formData.estimatedReach.toLocaleString()} contacts</div>
            </div>
            <div>
              <span className="text-blue-700 dark:text-blue-300">Expected Performance:</span>
              <div className="font-semibold text-blue-900 dark:text-blue-300">
                {selectedSegment.expectedPerformance ? (
                  <>
                    {Math.round(selectedSegment.expectedPerformance.openRate * 100)}% open rate
                  </>
                ) : (
                  'Performance data unavailable'
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Step 3: Template Selection Component
const TemplateSelectionStep = ({ formData, onFieldChange, templates, validationErrors }) => {
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const selectedTemplate = templates.find(t => t.id === formData.templateId);

  const handleTemplatePreview = (template) => {
    setPreviewTemplate(template);
    onFieldChange('templatePreview', template);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-content mb-4">Choose Email Template</h3>
        <p className="text-sm text-content-secondary mb-6">
          Select a template that matches your campaign goals and brand.
        </p>
      </div>

      {/* Template Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Email Template *
        </label>
        
        {templates.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 dark:bg-dark-700 rounded-lg border-2 border-dashed border-line-strong">
            <Mail className="w-12 h-12 mx-auto mb-4 text-content-subtle" />
            <h4 className="text-lg font-medium text-content mb-2">No Templates Available</h4>
            <p className="text-content-secondary mb-4">
              You need to create email templates before creating campaigns.
            </p>
            <button
              type="button"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Mail className="w-4 h-4" />
              Create Template
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
            {templates.map((template) => {
              const isSelected = formData.templateId === template.id;
              
              return (
                <div
                  key={template.id}
                  className={`border-2 rounded-lg overflow-hidden transition-all ${
                    isSelected
                      ? 'border-blue-500 ring-2 ring-blue-200'
                      : 'border-line hover:border-line-strong'
                  }`}
                >
                  {/* Template Preview */}
                  <div className="aspect-video bg-gray-100 dark:bg-dark-700 relative">
                    {template.thumbnail ? (
                      <img
                        src={template.thumbnail}
                        alt={template.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Mail className="w-8 h-8 text-content-subtle" />
                      </div>
                    )}
                    
                    {/* Preview Button */}
                    <button
                      type="button"
                      onClick={() => handleTemplatePreview(template)}
                      className="absolute top-2 right-2 p-2 bg-opacity-90 rounded-lg hover:bg-opacity-100 transition-all"
                    >
                      <Eye className="w-4 h-4 text-content-secondary" />
                    </button>
                  </div>
                  
                  {/* Template Info */}
                  <div className="p-3">
                    <h4 className="font-medium text-content mb-1">{template.name}</h4>
                    {template.description && (
                      <p className="text-sm text-content-secondary mb-2">{template.description}</p>
                    )}
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs text-content-muted">
                        {template.category && (
                          <span className="px-2 py-1 bg-gray-100 dark:bg-dark-700 rounded">{template.category}</span>
                        )}
                        {template.aiGenerated && (
                          <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded flex items-center gap-1">
                            <Brain className="w-3 h-3" />
                            AI
                          </span>
                        )}
                      </div>
                      
                      <button
                        type="button"
                        onClick={() => onFieldChange('templateId', template.id)}
                        className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                          isSelected
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-600'
                        }`}
                      >
                        {isSelected ? 'Selected' : 'Select'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {validationErrors.templateId && (
          <p className="mt-2 text-sm text-red-600 dark:text-red-300 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {validationErrors.templateId}
          </p>
        )}
      </div>

      {/* Selected Template Summary */}
      {selectedTemplate && (
        <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800/50 rounded-lg p-4">
          <h4 className="font-medium text-green-900 dark:text-green-300 mb-2">Selected Template</h4>
          <div className="flex items-center gap-3">
            <div className="w-16 h-12 bg-gray-100 dark:bg-dark-700 rounded overflow-hidden">
              {selectedTemplate.thumbnail ? (
                <img
                  src={selectedTemplate.thumbnail}
                  alt={selectedTemplate.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Mail className="w-4 h-4 text-content-subtle" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <div className="font-semibold text-green-900 dark:text-green-300">{selectedTemplate.name}</div>
              {selectedTemplate.description && (
                <div className="text-sm text-green-700 dark:text-green-300">{selectedTemplate.description}</div>
              )}
            </div>
            <button
              type="button"
              onClick={() => handleTemplatePreview(selectedTemplate)}
              className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/40 transition-colors text-sm"
            >
              Preview
            </button>
          </div>
        </div>
      )}

      {/* Template Preview Modal would go here */}
      {previewTemplate && (
        <TemplatePreviewModal
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
        />
      )}
    </div>
  );
};

// Step 4: AI Configuration Component
/** Shared field shell, so every A/B input reports its error the same way. */
const ABField = ({ id, label, hint, error, children }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
      {label}
    </label>
    {children}
    {hint && !error && <p className="mt-1 text-xs text-content-secondary">{hint}</p>}
    {error && (
      <p role="alert" className="mt-1 text-xs text-danger">
        {error}
      </p>
    )}
  </div>
);

/**
 * The A/B test a campaign is saved with.
 *
 * Variant A reuses the template picked on step 3 — `POST campaigns/ab-testing`
 * has no top-level template, so A's is simply that one — and each variant
 * contributes its own subject line.
 *
 * Two things are stated rather than left to be discovered: the split (half the
 * test share to each variant, the rest waiting for the winner) and the cost,
 * because the save deducts credits for the **whole** audience, not just the
 * test send.
 */
export const ABTestSection = ({ formData, onFieldChange, templates, audienceSize, validationErrors }) => {
  const enabled = formData.abTestingEnabled;
  const share = Number(formData.testPercentage) || 0;
  const perVariant = Math.round((share / 2) * 10) / 10;
  const reach = Number(audienceSize) || 0;
  const testedContacts = Math.round((reach * share) / 100);
  const variantATemplate = templates.find((t) => String(t.id) === String(formData.templateId));

  return (
    <div className="rounded-lg border border-line p-6">
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="ab-testing"
          checked={enabled}
          onChange={(e) => onFieldChange('abTestingEnabled', e.target.checked)}
          className="rounded border-line-strong text-purple-600 dark:text-purple-300 focus:ring-purple-500"
        />
        <label htmlFor="ab-testing" className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Test two versions and send the winner to the rest
        </label>
      </div>

      {!enabled && (
        <p className="mt-2 pl-7 text-xs text-content-secondary">
          Part of your audience receives two versions. After a set time the better
          performer is sent to everyone else.
        </p>
      )}

      {enabled && (
        <div className="mt-5 space-y-5">
          {/* ── variant A ── */}
          <div className="rounded-lg bg-surface-muted p-4">
            <h5 className="text-sm font-semibold text-content">Variant A</h5>
            <p className="mt-0.5 text-xs text-content-secondary">
              Template:{' '}
              <span className="font-medium text-content">
                {variantATemplate?.name || 'the one chosen in step 3'}
              </span>
            </p>
            <div className="mt-3">
              <ABField
                id="ab-subject-a"
                label="Subject line A"
                hint={`${formData.subjectA.length}/${SUBJECT_MAX}`}
                error={validationErrors.subjectA}
              >
                <input
                  id="ab-subject-a"
                  type="text"
                  maxLength={SUBJECT_MAX}
                  value={formData.subjectA}
                  onChange={(e) => onFieldChange('subjectA', e.target.value)}
                  placeholder="Your Diwali offer is here"
                  className="w-full px-3 py-2 border border-line rounded-lg bg-surface text-content focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </ABField>
            </div>
          </div>

          {/* ── variant B ── */}
          <div className="rounded-lg bg-surface-muted p-4">
            <h5 className="text-sm font-semibold text-content">Variant B</h5>
            <div className="mt-3 space-y-3">
              <ABField
                id="ab-template-b"
                label="Template B"
                error={validationErrors.variantBTemplateId}
              >
                <SelectField
                  id="ab-template-b"
                  value={formData.variantBTemplateId}
                  onChange={(e) => onFieldChange('variantBTemplateId', e.target.value)}
                  className="w-full"
                >
                  <option value="">Choose a template…</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </SelectField>
              </ABField>

              <ABField
                id="ab-subject-b"
                label="Subject line B"
                hint={`${formData.subjectB.length}/${SUBJECT_MAX}`}
                error={validationErrors.subjectB}
              >
                <input
                  id="ab-subject-b"
                  type="text"
                  maxLength={SUBJECT_MAX}
                  value={formData.subjectB}
                  onChange={(e) => onFieldChange('subjectB', e.target.value)}
                  placeholder="Don’t miss our Diwali offer"
                  className="w-full px-3 py-2 border border-line rounded-lg bg-surface text-content focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </ABField>
            </div>
          </div>

          {variantsAreIdentical(formData) && (
            <p
              role="status"
              className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800/50 dark:bg-amber-950/40 dark:text-amber-300"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>
                Both variants use the same template and the same subject line, so the
                test cannot tell them apart. The whole audience is charged either way,
                and a tie goes to variant A.
              </span>
            </p>
          )}

          {/* ── how the test runs ── */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <ABField
              id="ab-share"
              label="Test share"
              error={validationErrors.testPercentage}
            >
              <div className="flex items-center gap-2">
                <input
                  id="ab-share"
                  type="number"
                  min={1}
                  max={100}
                  value={formData.testPercentage}
                  onChange={(e) => onFieldChange('testPercentage', e.target.value === '' ? '' : Number(e.target.value))}
                  className="w-full px-3 py-2 border border-line rounded-lg bg-surface text-content focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <span className="text-sm text-content-secondary">%</span>
              </div>
            </ABField>

            <ABField
              id="ab-hours"
              label="Decide after"
              hint="Hours to wait before picking the winner"
              error={validationErrors.evaluationHours}
            >
              <div className="flex items-center gap-2">
                <input
                  id="ab-hours"
                  type="number"
                  min={1}
                  value={formData.evaluationHours}
                  onChange={(e) => onFieldChange('evaluationHours', e.target.value === '' ? '' : Number(e.target.value))}
                  className="w-full px-3 py-2 border border-line rounded-lg bg-surface text-content focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <span className="text-sm text-content-secondary">hrs</span>
              </div>
            </ABField>

            <ABField
              id="ab-metric"
              label="Winner is decided by"
              error={validationErrors.winnerMetric}
            >
              <SelectField
                id="ab-metric"
                value={formData.winnerMetric}
                onChange={(e) => onFieldChange('winnerMetric', e.target.value)}
                className="w-full"
              >
                <option value="opens">Opens</option>
                <option value="clicks">Clicks</option>
              </SelectField>
            </ABField>
          </div>

          {/* What the numbers above actually mean, and what it costs. */}
          <div className="rounded-lg border border-line bg-surface-muted p-4 text-sm">
            <p className="font-medium text-content">How this will run</p>
            <p className="mt-1 text-content-secondary">
              {perVariant}% gets variant A and {perVariant}% gets variant B
              {reach > 0 && <> — about {testedContacts.toLocaleString()} contacts in all</>}. After{' '}
              {formData.evaluationHours || 0} hour
              {Number(formData.evaluationHours) === 1 ? '' : 's'}, whichever has more{' '}
              {formData.winnerMetric} goes to the remaining {Math.max(0, 100 - share)}%.
            </p>
            <p className="mt-2 text-content-secondary">
              {/* The backend charges at save time for everyone, test and winner alike. */}
              <span className="font-medium text-content">Credits:</span> the whole audience
              {reach > 0 && <> ({reach.toLocaleString()} contacts)</>} is charged when the campaign
              is saved, not just the test send.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

const AIConfigurationStep = ({
  formData,
  onFieldChange,
  templates = [],
  audienceSize = 0,
  validationErrors,
}) => {

  const handleSchedulingTypeChange = (type) => {
    onFieldChange('schedulingType', type);
    if (type !== 'scheduled') {
      onFieldChange('scheduledAt', '');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-content mb-4">Testing &amp; Scheduling</h3>
        <p className="text-sm text-content-secondary mb-6">
          Configure AI automation and scheduling options for your campaign.
        </p>
      </div>

      {/* An "AI-Powered Optimization" block used to sit here, with an agent
          picker and a confidence score. No API in the product returns agents,
          so the list was always empty, and neither save endpoint carries an
          agent — switching it on changed nothing about the campaign. It was
          also what made this step unsubmittable, because the agent field was
          required against that empty list. It comes back when there is an
          endpoint behind it. */}

      <ABTestSection
        formData={formData}
        onFieldChange={onFieldChange}
        templates={templates}
        audienceSize={audienceSize}
        validationErrors={validationErrors}
      />

      {/* Scheduling Options */}
      <div>
        <h4 className="font-medium text-content mb-3">Campaign Scheduling</h4>
        <div className="space-y-3">
          {[
            {
              value: 'immediate',
              label: 'Send Immediately',
              description: 'Campaign will be sent as soon as it\'s created',
              icon: Play
            },
            {
              value: 'scheduled',
              label: 'Schedule for Later',
              description: 'Choose a specific date and time to send',
              icon: Clock
            },
            /* "Automated Triggers" used to be the third option here. Neither
               save endpoint can express a trigger-driven send — both take a
               start and an end date and nothing else — and picking it fell
               through to `new Date()`, so the campaign went out immediately
               while the user believed they had set up a trigger. It comes back
               the day the backend carries it. */
          ].map((option) => {
            const Icon = option.icon;
            const isSelected = formData.schedulingType === option.value;
            
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSchedulingTypeChange(option.value)}
                className={`w-full p-4 border rounded-lg text-left transition-all ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                    : 'border-line hover:border-line-strong hover:bg-gray-50 dark:hover:bg-dark-700'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    isSelected ? 'bg-blue-100' : 'bg-gray-100 dark:bg-dark-700'
                  }`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-content">{option.label}</div>
                    <div className="text-sm text-content-secondary">{option.description}</div>
                  </div>
                  {isSelected && (
                    <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Scheduled Date/Time Input */}
        {formData.schedulingType === 'scheduled' && (
          <div className="mt-4">
            <label htmlFor="scheduled-at" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Send Date & Time *
            </label>
            <input
              id="scheduled-at"
              type="datetime-local"
              value={formData.scheduledAt}
              onChange={(e) => onFieldChange('scheduledAt', e.target.value)}
              min={new Date().toISOString().slice(0, 16)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                validationErrors.scheduledAt ? 'border-red-300 dark:border-red-800/50 focus:ring-red-500' : 'border-line-strong'
              }`}
            />
            {validationErrors.scheduledAt && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-300 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {validationErrors.scheduledAt}
              </p>
            )}
          </div>
        )}

        {/* Automation Triggers (placeholder for future implementation) */}
      </div>

      {/* Configuration Summary */}
      {(formData.abTestingEnabled || formData.schedulingType !== 'immediate') && (
        <div className="bg-gray-50 dark:bg-dark-700 border border-line rounded-lg p-4">
          <h4 className="font-medium text-content mb-3">Configuration Summary</h4>
          <div className="space-y-2 text-sm">
            {formData.abTestingEnabled && (
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-blue-600 dark:text-blue-300" />
                <span>A/B Testing: <strong>Enabled</strong></span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-green-600 dark:text-green-300" />
              <span>
                Scheduling: <strong>
                  {formData.schedulingType === 'immediate' && 'Send Immediately'}
                  {formData.schedulingType === 'scheduled' && `Scheduled for ${formData.scheduledAt ? new Date(formData.scheduledAt).toLocaleString() : 'Not set'}`}
                </strong>
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Template Preview Modal Component
const TemplatePreviewModal = ({ template, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/60 dark:bg-black/75 backdrop-blur-[3px] flex items-center justify-center z-50 p-4">
      <div className="bg-surface dark:bg-dark-800 border border-line dark:border-dark-700 rounded-2xl shadow-2xl dark:shadow-[0_20px_60px_rgba(0,0,0,0.5)] w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-line">
          <h3 className="text-lg font-semibold text-content">Template Preview</h3>
          <button aria-label="Close"
            onClick={onClose}
            className="text-content-subtle hover:text-content-secondary transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          <div className="mb-4">
            <h4 className="font-medium text-content">{template.name}</h4>
            {template.description && (
              <p className="text-sm text-content-secondary mt-1">{template.description}</p>
            )}
          </div>
          
          {/* Template preview content would go here */}
          <div className="bg-gray-100 dark:bg-dark-700 rounded-lg p-8 text-center">
            <Mail className="w-16 h-16 mx-auto mb-4 text-content-subtle" />
            <p className="text-content-secondary">Template preview would be rendered here</p>
            <p className="text-sm text-content-muted mt-2">
              This would show the actual email template with sample content
            </p>
          </div>
        </div>
        
        <div className="p-4 border-t border-line">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors"
          >
            Close Preview
          </button>
        </div>
      </div>
    </div>
  );
};